import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {EmojiItem} from "@tiptap/extension-emoji";
import {Fragment, forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {useTranslation} from "react-i18next";
import {FLOATING_SHADOW} from "../../../newVariablePicker/shared/floatingShadow";
import {PickerKeyboardHints, usePickerNavigation} from "../../../pickers/PickerNavigation";
import {EMOJI_PAGE_SIZE, emojiGroupTranslationKey, emojiLabel} from "./emojiCatalog";

export interface EmojiSuggestionListHandle {
    onKeyDown: (event: KeyboardEvent) => boolean;
}

export interface EmojiSuggestionListProps {
    items: EmojiItem[];
    query: string;
    onSelect: (emoji: EmojiItem) => void;
}

const LIST_WIDTH = 380;

/** The popup after `:` in the text. The list grows by a page as it scrolls, so a short query stays fast. */
const EmojiSuggestionList = forwardRef<EmojiSuggestionListHandle, EmojiSuggestionListProps>((
    {items: matches, query, onSelect},
    ref,
) => {
    const {t} = useTranslation();
    const [visibleCount, setVisibleCount] = useState(EMOJI_PAGE_SIZE);
    const items = matches.slice(0, visibleCount);
    const hasMore = items.length < matches.length;
    const {selectedIndex, listRef, selectFromPointer, handleKey} = usePickerNavigation({
        items,
        onSelect,
        resetKey: query,
    });

    useEffect(() => setVisibleCount(EMOJI_PAGE_SIZE), [query]);

    useImperativeHandle(ref, () => ({
        onKeyDown: (event: KeyboardEvent) => handleKey(event.key),
    }));

    const counts = matches.reduce<Record<string, number>>((totals, item) => {
        const group = item.group ?? '';
        totals[group] = (totals[group] ?? 0) + 1;
        return totals;
    }, {});

    let renderedGroup: string | undefined;

    return (
        <Paper
            variant="outlined"
            sx={{
                width: LIST_WIDTH,
                maxWidth: "calc(100vw - 32px)",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: FLOATING_SHADOW,
            }}
            onKeyDown={event => {
                if (handleKey(event.key)) event.preventDefault();
            }}
        >
            <MenuList
                ref={listRef}
                disablePadding
                onScroll={event => {
                    if (!hasMore) return;
                    const list = event.currentTarget;
                    const remaining = list.scrollHeight - list.scrollTop - list.clientHeight;
                    if (remaining <= 160) {
                        setVisibleCount(count => Math.min(count + EMOJI_PAGE_SIZE, matches.length));
                    }
                }}
                sx={{maxHeight: 340, overflowY: "auto"}}
            >
                {items.map((item, index) => {
                    const group = item.group ?? '';
                    const startsGroup = group !== renderedGroup;
                    renderedGroup = group;

                    return (
                        <Fragment key={item.name}>
                            {startsGroup && (
                                <Stack
                                    component="li"
                                    direction="row"
                                    alignItems="baseline"
                                    gap={0.75}
                                    sx={{px: 2, pt: 1.5, pb: 0.5}}
                                >
                                    <Typography variant="overline" color="text.secondary">
                                        {t(emojiGroupTranslationKey(group))}
                                    </Typography>
                                    <Typography variant="overline" color="text.disabled">
                                        {counts[group]}
                                    </Typography>
                                </Stack>
                            )}

                            <MenuItem
                                data-index={index}
                                selected={index === selectedIndex}
                                onMouseMove={event => selectFromPointer(index, event.clientX, event.clientY)}
                                // The text keeps its focus, so the pick lands where the `:` was typed.
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => onSelect(item)}
                                sx={{
                                    gap: 1.5,
                                    py: 0.5,
                                    pr: 2,
                                    "&:hover": {backgroundColor: "transparent"},
                                    "&.Mui-selected, &.Mui-selected:hover": {backgroundColor: "action.hover"},
                                }}
                            >
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        flexShrink: 0,
                                        width: 32,
                                        textAlign: "center",
                                        fontSize: 22,
                                        lineHeight: 1.4,
                                        fontFamily: "Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif",
                                    }}
                                >
                                    {item.emoji}
                                </Box>
                                <Typography variant="body2" noWrap sx={{flex: 1, minWidth: 0}}>
                                    {emojiLabel(item)}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    noWrap
                                    sx={{maxWidth: 140, minWidth: 0, textAlign: "right"}}
                                >
                                    :{item.shortcodes[0] ?? item.name}:
                                </Typography>
                            </MenuItem>
                        </Fragment>
                    );
                })}

                {items.length === 0 && (
                    <Box sx={{px: 2, py: 3, textAlign: "center"}}>
                        <Typography variant="body2" color="text.secondary">
                            {t('No emoji matches "{{query}}"', {query: query.trim()})}
                        </Typography>
                    </Box>
                )}
            </MenuList>

            <PickerKeyboardHints/>
        </Paper>
    );
});

EmojiSuggestionList.displayName = "EmojiSuggestionList";

export default EmojiSuggestionList;
